/*!
 * Copyright (c) REWE Digital GmbH
 */
import { h, Host } from "@stencil/core";
import { isContextEvent, isOutsideEvent, isTouchDevice } from "@rdss/core";
/**
 * VxDrawer is a stencil component that creates a drawer UI element
 * that can be used, for example, to display a component in a dropdown.
 * The drawer can be opened or closed based on the 'open' prop.
 *
 * @example
 * <vx-drawer>
 *   <a slot="trigger" href="/account" aria-label={this.snippets.accountButtonLabel}>
 *     <vx-icon-button aria-label={this.snippets.accountButtonLabel} icon="person"/>
 *   </a>
 *   <wf-account-menu slot="content"/>
 * </vx-drawer>
 */
// eslint-disable-next-line require-jsdoc
export class VxDrawer {
    constructor() {
        /**
         * Checks if the current device is a touch device and sets the 'isTouchDevice' state accordingly.
         */
        this.checkIsTouchDevice = () => (this.isTouchDevice = isTouchDevice());
        this.open = undefined;
        this.keepOpenOnOutsideClick = undefined;
        this.backdrop = false;
        this.isTouchDevice = undefined;
    }
    /**
     * @inheritdoc
     */
    componentWillLoad() {
        this.checkIsTouchDevice();
    }
    /**
     * Dispatches the visibility state to the child component.
     */
    handleOpenState(isOpened) {
        this.host.dispatchEvent(new CustomEvent('drawerVisibilityChanged', { detail: isOpened, bubbles: true }));
    }
    /**
     * Handles click event. If the device is a touch device,
     * it prevents the default action and sets the 'open' prop to true,
     * opening the drawer.
     */
    handleClick(e) {
        if (!this.isTouchDevice)
            return;
        const contentClicked = isContextEvent(e, this.contentElement);
        const backdropClicked = isContextEvent(e, this.backdropElement);
        const triggerClicked = isContextEvent(e, this.triggerElement);
        if (backdropClicked)
            this.open = false;
        if (triggerClicked)
            this.open = !this.open;
        if (contentClicked)
            return (this.open = true);
        e.preventDefault();
    }
    /**
     * Handles the 'click' event on the document. If the event occurs outside the host element,
     * it sets the 'open' prop to false, closing the drawer.
     */
    handleOutsideClick(e) {
        const outsideHostClicked = isOutsideEvent(e, this.host);
        if (outsideHostClicked) {
            this.open = this.keepOpenOnOutsideClick;
        }
    }
    /**
     * Handles outside events. If the event occurs outside the host element,
     * it sets the 'open' prop to false, closing the drawer.
     */
    handleOutsideEvents(e) {
        const outsideContentClicked = !isContextEvent(e, this.contentElement);
        if (outsideContentClicked) {
            this.open = false;
        }
    }
    /**
     * Renders the component, including a slot for a trigger and a slot for content.
     * The host element gets a class based on whether the drawer is open and
     * whether the device is a touch device.
     */
    render({ open } = this) {
        return (h(Host, { key: '5f7fc3fa2824d7c11301f11f97d1669d38900612', class: { open, 'touch-device': this.isTouchDevice } }, this.backdrop && h("div", { class: "backdrop", ref: el => (this.backdropElement = el) }), h("div", { key: 'b15a5f313d538e0dd249d728829e5da9fab9ea5f', class: "trigger", ref: el => (this.triggerElement = el) }, h("slot", { key: 'd6765c2655faa761185ca2f93c232f0eaef32132', name: "trigger" })), h("div", { key: '171f4a493b758869fd75f894b8ab2ade17f14ae6', class: "wrapper" }, h("div", { key: '7ae1e7e0fc114cc2bd2ddbdce625a49d74ccf97c', class: "content", ref: el => (this.contentElement = el) }, h("slot", { key: 'fc721d2394f99af17375e9526e3e4a7daa91bf72', name: "content" })))));
    }
    static get is() { return "vx-drawer"; }
    static get encapsulation() { return "scoped"; }
    static get originalStyleUrls() {
        return {
            "$": ["vx-drawer.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["vx-drawer.css"]
        };
    }
    static get properties() {
        return {
            "open": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The 'open' prop determines whether the drawer is open or not."
                },
                "attribute": "open",
                "reflect": false
            },
            "keepOpenOnOutsideClick": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The 'keepOpenOnTouchstartOutside' prop determines whether\nthe drawer should remain open when a touchstart event occurs\noutside the host element."
                },
                "attribute": "keep-open-on-outside-click",
                "reflect": false
            },
            "backdrop": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The 'backdrop' prop determines whether a backdrop is displayed behind the drawer when it's open.\nThe backdrop is a semi-transparent overlay that covers the entire viewport."
                },
                "attribute": "backdrop",
                "reflect": false,
                "defaultValue": "false"
            }
        };
    }
    static get states() {
        return {
            "isTouchDevice": {}
        };
    }
    static get elementRef() { return "host"; }
    static get watchers() {
        return [{
                "propName": "open",
                "methodName": "handleOpenState"
            }];
    }
    static get listeners() {
        return [{
                "name": "click",
                "method": "handleClick",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "click",
                "method": "handleOutsideClick",
                "target": "document",
                "capture": false,
                "passive": false
            }, {
                "name": "touchmove",
                "method": "handleOutsideEvents",
                "target": "document",
                "capture": false,
                "passive": true
            }];
    }
}
//# sourceMappingURL=vx-drawer.js.map
